import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ProjectDocument } from '../../shared/interfaces/project/document/document.interface'
import { FilePond } from 'react-filepond'
import Uploader from './uploader'
import Dropzone from 'react-dropzone'
import { FilePondFile } from 'filepond'
import {
  ArrowUpTrayIcon,
  XMarkIcon,
  FolderIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline'
import { DocumentArrowUpIcon } from '@heroicons/react/24/solid'
import { useParams, useSearchParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import { usePrompt } from '../../hooks/use-prompt'
import DuplicatorStatus from '../duplicator/duplicator-status'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCurrentProject,
  selectScTemplateDocument,
  setModal,
  setScTemplateDocument,
} from '../../redux/application-slice'
import DocumentManagerDragDrop from '../document-manager/document-manager-drag-drop'
import {
  useDeleteDocumentMutation,
  useGetDocumentsByProjectQuery,
  useMarkDocumentAsScTemplateMutation,
  useMoveItemsIntoFolderMutation,
  useUpdateDocumentSortMutation,
} from '../../redux/api-slice'
import { toast } from 'react-toastify'
import FolderHeader from './folder-header'
import { MODAL_TYPES } from '../modals/modal-controller'
import { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import ProjectUploadStatus from './project-upload-status'
import DocumentSearchInput from './document-search-input'
import { Button, Modal, Radio } from '@mantine/core'
import { RiskListTemplateBanner } from '../banners/specific-banners'
import { usePostHog } from 'posthog-js/react'
import { POSTHOG } from '../../utils/posthog-constants'
import { useGetUserProfileQuery } from '../../redux/api-slice'
import { useLocalStorage } from '@mantine/hooks'

const DocumentList: React.FC = () => {
  const posthog = usePostHog()
  const dispatch = useDispatch()
  const dragUploaderRef = useRef<FilePond | null>(null)
  const [files, setFiles] = useState<FilePondFile[]>([])
  const [uploaderVisible, setUploaderVisible] = useState(true)
  const { projectId } = useParams<{ projectId: string }>()
  const currentProject = useSelector(selectCurrentProject)
  const [deleteDocument] = useDeleteDocumentMutation()
  const [updateDocumentSort] = useUpdateDocumentSortMutation()
  const [moveItemsIntoFolder] = useMoveItemsIntoFolderMutation()
  const [fileStatus, setFileStatus] = useState({})
  const [activeRequests, setActiveRequests] = useState({})
  const [searchParams] = useSearchParams()
  const [fileProgressRef, setFileProgressRef] = useState<{
    [fileName: string]: {
      lengthComputable: boolean
      loaded: number
      total: number
    }
  }>({})
  const [scTemplateModalOpen, setScTemplateModalOpen] = useState(false)
  const [selectedScDocumentId, setSelectedScDocumentId] = useState<
    number | null
  >(null)
  const scTemplateDocumentId = useSelector(selectScTemplateDocument)
  const [markAsScTemplate] = useMarkDocumentAsScTemplateMutation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [showProcessSupplementaryConditionsModal] = useLocalStorage<boolean>({
    key: 'show-process-sc-modal',
    defaultValue: true,
    getInitialValueInEffect: true,
  })
  const { data: user } = useGetUserProfileQuery(undefined)

  const folderId = useMemo(() => {
    return searchParams.get('folderId')
  }, [searchParams])

  const {
    currentData: documentResponse,
    isLoading: documentsAndFoldersLoading,
  } = useGetDocumentsByProjectQuery(
    currentProject?.id
      ? { projectId: currentProject?.id, folderId: folderId ?? null }
      : skipToken,
    {
      pollingInterval: 10000,
    }
  )

  const allFilesUploaded = useMemo(() => {
    if (!files.length) {
      return true
    }
    return Object.values(fileProgressRef).every((progress) => {
      return progress.loaded === progress.total
    })
  }, [fileProgressRef, files])

  //unstable but fine for our purposes https://reactrouter.com/en/main/hooks/use-prompt
  usePrompt({ when: !allFilesUploaded })

  useEffect(() => {
    setFiles([])
  }, [projectId])

  useEffect(() => {
    if (documentResponse?.documents?.length) {
      // Find document marked as template
      const scTemplateDoc = documentResponse.documents.find((doc) =>
        doc?.classifications?.some(
          (classification) =>
            classification.classification === 'TEMPLATE_DOCUMENT'
        )
      )

      if (scTemplateDoc && scTemplateDoc.id !== scTemplateDocumentId) {
        dispatch(setScTemplateDocument(scTemplateDoc.id))
        posthog.capture(POSTHOG.document_marked_as_template, {
          projectId: currentProject?.id,
          documentId: scTemplateDoc.id,
        })
      }
    }
  }, [documentResponse?.documents, dispatch, scTemplateDocumentId])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const filesToUpload = acceptedFiles.filter(
      (f) =>
        f.type ===
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        f.type === 'application/pdf'
    )
    if (!dragUploaderRef) {
      return
    }
    dragUploaderRef?.current?.addFiles(filesToUpload)
  }, [])

  const calculateProgressPercentage = useCallback(
    (file) => {
      if (!file || !file.filename) {
        return 0
      }

      const loaded = fileProgressRef?.[file.filename]?.loaded ?? 0
      const total = fileProgressRef?.[file.filename]?.total ?? 0
      if (!total) {
        return 0
      }
      return Math.min(Math.round((loaded / total) * 100), 100)
    },
    [fileProgressRef]
  )

  const onDocumentUploaded = useCallback(() => {
    if (
      user?.feature_flags?.check_for_supplementary_conditions &&
      showProcessSupplementaryConditionsModal
    ) {
      dispatch(
        setModal({ modal: MODAL_TYPES.PROCESS_SUPPLEMENTARY_CONDITIONS })
      )
    } else {
      dispatch(
        setModal({ modal: MODAL_TYPES.EDIT_PROJECT_RISK_LIST_TEMPLATES })
      )
    }
  }, [
    dispatch,
    showProcessSupplementaryConditionsModal,
    user?.feature_flags?.check_for_supplementary_conditions,
  ])

  const calculateProgressWidth = (file: FilePondFile) => {
    if (!file || !file.filename) {
      return '0%'
    }

    const loaded = fileProgressRef?.[file.filename]?.loaded ?? 0
    const total = fileProgressRef?.[file.filename]?.total ?? 0
    if (!total) {
      return '0%'
    }
    return `${(loaded / total) * 100}%`
  }

  // Handler for marking a document as template
  const handleMarkAsScTemplate = useCallback(async () => {
    if (!selectedScDocumentId) {
      toast.error('Please select a document to mark as template document')
      return
    }

    try {
      // Call the API to mark the document as template
      await markAsScTemplate(selectedScDocumentId).unwrap()

      // Update Redux state
      dispatch(setScTemplateDocument(selectedScDocumentId))

      // Show success toast
      toast.success('Document marked as template')

      // Close the modal
      setScTemplateModalOpen(false)
    } catch (error) {
      console.error('Failed to mark document as template:', error)
      toast.error('Failed to mark document as template')
    }
  }, [selectedScDocumentId, markAsScTemplate, dispatch])

  // Handler for radio button change
  const handleScDocumentChange = useCallback((documentId: number) => {
    setSelectedScDocumentId(documentId)
  }, [])

  const onClickUpload = useCallback(() => {
    dragUploaderRef?.current?.browse()
  }, [dragUploaderRef])

  const onClickOpenFolder = useCallback(() => {
    dispatch(
      setModal({
        modal: MODAL_TYPES.CREATE_FOLDER,
        folder: documentResponse?.folder?.id,
      })
    )
  }, [dispatch, documentResponse?.folder?.id])

  const onCloseUploader = useCallback(() => {
    setUploaderVisible(false)
    setFiles([])
  }, [])

  const onProgressUpdate = useCallback(
    (fileName, progress, status) => {
      if (!fileProgressRef[fileName]) {
        setUploaderVisible(true)
      }
      setFileProgressRef((prev) => ({
        ...prev,
        [fileName]: {
          lengthComputable: progress.lengthComputable,
          loaded: progress.loaded,
          total: progress.total,
        },
      }))

      if (status) {
        setFileStatus((prev) => ({
          ...prev,
          [fileName]: status,
        }))
      }
    },
    [fileProgressRef]
  )

  const retryUpload = useCallback(
    (file) => {
      if (!file || !file.filename) {
        return
      }
      // Remove the file from the current list and add it again
      const fileToRetry = files.find((f) => f.filename === file.filename)
      if (fileToRetry && dragUploaderRef.current) {
        // Remove the failed file
        setFiles((prev) => prev.filter((f) => f.filename !== file.filename))
        setFileStatus((prev) => {
          const newStatus = { ...prev }
          delete newStatus[file.filename]
          return newStatus
        })
        setFileProgressRef((prev) => {
          const newProgress = { ...prev }
          delete newProgress[file.filename]
          return newProgress
        })

        // Re-add the file for upload after a short delay
        setTimeout(() => {
          if (dragUploaderRef.current) {
            dragUploaderRef.current.addFile(fileToRetry.file)
          }
        }, 100)
      }
    },
    [files]
  )

  const _sortDocuments = useCallback(
    async (
      foundActiveDocumentIndex: number | undefined,
      foundOverDocumentIndex: number | undefined
    ) => {
      if (
        foundActiveDocumentIndex === undefined ||
        foundOverDocumentIndex === undefined ||
        !currentProject?.id ||
        !documentResponse?.documents
      ) {
        return
      }
      let reorderedDocuments: ProjectDocument[] = [
        ...(documentResponse?.documents ?? []),
      ]
      reorderedDocuments = arrayMove(
        reorderedDocuments,
        foundActiveDocumentIndex,
        foundOverDocumentIndex
      )
      const documentIndexes = reorderedDocuments.reduce((acc, item, index) => {
        acc[item.id] = index
        return acc
      }, {})

      await updateDocumentSort({
        projectId: currentProject?.id,
        documentIndexes: documentIndexes,
        folderId: documentResponse?.folder?.id,
      })
    },
    [
      currentProject?.id,
      documentResponse?.documents,
      documentResponse?.folder?.id,
      updateDocumentSort,
    ]
  )

  const documentSearchQuery = useMemo(() => {
    return searchParams.get('search')
  }, [searchParams])

  const registerUploadRequest = useCallback((fileName, requestController) => {
    setActiveRequests((prev) => ({
      ...prev,
      [fileName]: requestController,
    }))
  }, [])

  const cancelUpload = useCallback(
    async (file) => {
      if (!file || !file.filename) {
        return
      }

      const request = activeRequests[file.filename]
      if (request) {
        request.abort()
      }

      // Update file status to indicate it was cancelled
      setFileStatus((prev) => ({
        ...prev,
        [file.filename]: 'cancelled',
      }))

      posthog.capture('document_upload_cancelled', {
        projectId: currentProject?.id,
        fileName: file.filename,
        fileSize: file.fileSize,
        fileType: file.fileType,
        partiallyUploaded: !!fileProgressRef?.[file.filename]?.loaded,
        uploadProgress: calculateProgressPercentage(file),
      })

      const associatedDocument = documentResponse?.documents?.find(
        (doc) => doc.title === file.filename
      )

      if (associatedDocument?.uuid && currentProject?.id) {
        try {
          await deleteDocument({
            uuid: associatedDocument.uuid,
            project: currentProject.id,
            folder: documentResponse?.folder,
            search_query: documentSearchQuery || undefined,
          }).unwrap()
          toast.success('Document deleted successfully')
        } catch (error) {
          toast.error('Failed to delete document')
          console.error('Error deleting document:', error)
        }
      }

      // Remove from files list
      setFiles((prev) => prev.filter((f) => f.filename !== file.filename))
      setFileProgressRef((prev) => {
        const newProgress = { ...prev }
        delete newProgress[file.filename]
        return newProgress
      })
      setActiveRequests((prev) => {
        const newRequests = { ...prev }
        delete newRequests[file.filename]
        return newRequests
      })
    },
    [
      activeRequests,
      deleteDocument,
      documentResponse?.documents,
      documentResponse?.folder,
      currentProject?.id,
      documentSearchQuery,
      fileProgressRef,
      posthog,
      calculateProgressPercentage,
    ]
  )

  const handleDrop = useCallback(
    // eslint-disable-next-line complexity
    async (dropResult: DragEndEvent) => {
      const { active, over } = dropResult
      if (!active || !over) {
        return
      }
      const foundActiveDocumentIndex = documentResponse?.documents?.findIndex(
        (d) => d.id === active.id
      )
      const foundOverDocumentIndex = documentResponse?.documents?.findIndex(
        (d) => d.id === over.id
      )
      const foundOverFolderIndex = documentResponse?.folders?.findIndex(
        (f) => f.id === over.id
      )
      const foundActiveFolderIndex = documentResponse?.folders?.findIndex(
        (f) => f.id === active.id
      )
      //Case when dragging folder where target is folder up one level
      if (
        over.id === documentResponse?.folder?.id &&
        foundActiveFolderIndex !== -1
      ) {
        if (foundActiveFolderIndex === undefined || !currentProject?.id) {
          return
        }
        const foundFolder = documentResponse?.folders[foundActiveFolderIndex]
        await moveItemsIntoFolder({
          destination_folder_id:
            documentResponse?.folder.folder?.id ?? undefined,
          document_ids: [],
          folder_ids: [foundFolder.id],
          projectId: currentProject?.id,
          folderId: documentResponse?.folder?.id,
          searchQuery: documentSearchQuery ?? undefined,
        }).unwrap()
        toast.success('Folder moved into parent folder')
        return
      }
      if (
        over.id === documentResponse?.folder?.id &&
        foundActiveDocumentIndex !== -1
      ) {
        if (
          foundActiveDocumentIndex === undefined ||
          foundOverFolderIndex === undefined ||
          !currentProject?.id ||
          !documentResponse?.documents
        ) {
          return
        }
        const foundDocument =
          documentResponse?.documents[foundActiveDocumentIndex]
        await moveItemsIntoFolder({
          destination_folder_id:
            documentResponse?.folder.folder?.id ?? undefined,
          document_ids: [foundDocument.id],
          folder_ids: [],
          projectId: currentProject?.id,
          folderId: documentResponse?.folder?.id,
        }).unwrap()
        toast.success('Document moved into folder')
        return
      }
      //Case when both target and origin are documents, sort
      if (foundActiveDocumentIndex !== -1 && foundOverDocumentIndex !== -1) {
        _sortDocuments(foundActiveDocumentIndex, foundOverDocumentIndex)
      }
      //Case when dragging a document into a folder
      else if (foundActiveDocumentIndex !== -1 && foundOverFolderIndex !== -1) {
        if (
          foundActiveDocumentIndex === undefined ||
          foundOverFolderIndex === undefined ||
          !currentProject?.id ||
          !documentResponse?.documents
        ) {
          return
        }
        const foundDocument =
          documentResponse?.documents[foundActiveDocumentIndex]
        await moveItemsIntoFolder({
          destination_folder_id: over.id.toString(),
          document_ids: [foundDocument.id],
          folder_ids: [],
          projectId: currentProject?.id,
          folderId: documentResponse?.folder?.id,
        }).unwrap()
        toast.success('Document moved into folder')
      }
      //Case when dragging a folder into a folder
      else if (
        foundActiveFolderIndex !== -1 &&
        foundOverFolderIndex !== -1 &&
        foundActiveFolderIndex !== foundOverFolderIndex
      ) {
        if (
          foundActiveDocumentIndex === undefined ||
          foundOverFolderIndex === undefined ||
          !currentProject?.id ||
          !documentResponse?.documents
        ) {
          return
        }
        await moveItemsIntoFolder({
          destination_folder_id: over.id.toString(),
          document_ids: [],
          folder_ids: [active.id.toString()],
          projectId: currentProject?.id,
          folderId: documentResponse?.folder?.id,
        }).unwrap()
        toast.success('Folder moved successfully')
      }
    },
    [
      documentResponse,
      currentProject?.id,
      moveItemsIntoFolder,
      documentSearchQuery,
      _sortDocuments,
    ]
  )

  const renderDocumentListHeader = useMemo(() => {
    if (documentResponse?.folder?.name) {
      return <FolderHeader folder={documentResponse?.folder} />
    } else {
      return currentProject?.title ? (
        <div>{currentProject?.title}</div>
      ) : (
        <div className="animate-pulse rounded-md bg-gray-200 px-8 py-2" />
      )
    }
  }, [currentProject?.title, documentResponse?.folder])

  const onClickSupplementaryConditionsRunAnalysis = useCallback(() => {
    dispatch(setModal({ modal: MODAL_TYPES.SUPPLEMENTARY_CONDITIONS }))
    setMenuOpen(false)
  }, [dispatch])

  return (
    <div className="flex grow flex-col overflow-hidden bg-gray-50">
      <DuplicatorStatus
        revisionParams={
          currentProject?.id ? { projectId: currentProject?.id } : skipToken
        }
      />
      <div className="flex w-full justify-between border-b bg-gray-100 p-1.5 pr-4">
        <DocumentSearchInput />
        <div className="flex gap-2">
          <Button
            disabled={!!documentSearchQuery}
            onClick={onClickOpenFolder}
            variant="default"
            leftSection={<FolderIcon className="h-5 w-5 text-gray-700" />}
          >
            Create Folder
          </Button>

          <Button
            id="upload-files-button"
            onClick={onClickUpload}
            leftSection={<DocumentArrowUpIcon className="h-5 w-5" />}
            color="blue"
          >
            Upload Files
          </Button>

          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center justify-center rounded p-1.5 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Open options menu"
            >
              <EllipsisVerticalIcon className="h-5 w-5 text-gray-600" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full z-10 mt-1 w-72 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                <button
                  onClick={onClickSupplementaryConditionsRunAnalysis}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                >
                  Analyze supplementary conditions
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        opened={scTemplateModalOpen}
        onClose={() => setScTemplateModalOpen(false)}
        title="Mark Document as Template"
        centered
      >
        <p className="mb-4 text-sm text-gray-600">
          Select a document to mark as the template. This document will be used
          as the reference for processing supplementary conditions.
        </p>

        <Radio.Group
          value={selectedScDocumentId?.toString() || ''}
          onChange={(value) => handleScDocumentChange(Number(value))}
        >
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {documentResponse?.documents?.map((doc) => (
              <Radio
                key={doc.id}
                value={doc.id.toString()}
                label={
                  <div className="flex items-center">
                    <span className="truncate text-sm" title={doc.title}>
                      {doc.title}
                    </span>
                    {doc.id === scTemplateDocumentId && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                        Current Template
                      </span>
                    )}
                  </div>
                }
              />
            ))}
          </div>
        </Radio.Group>

        <div className="mt-4 flex justify-end gap-3">
          <Button
            variant="default"
            onClick={() => setScTemplateModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMarkAsScTemplate}
            disabled={
              !documentResponse?.documents?.length ||
              !documentResponse?.documents?.some((doc) =>
                doc?.classifications?.some(
                  (classification) =>
                    classification.classification === 'SUPPLEMENTARY_CONDITIONS'
                )
              )
            }
            color="blue"
          >
            Mark as Template
          </Button>
        </div>
      </Modal>

      <ProjectUploadStatus />
      <RiskListTemplateBanner />
      <div className="flex shrink-0 items-center justify-between gap-2 overflow-hidden p-2 px-4 text-sm">
        <div className="shrink-0">{renderDocumentListHeader}</div>
      </div>
      <Dropzone noClick onDrop={onDrop}>
        {({ getRootProps, getInputProps }) => (
          <div
            data-playwright="document-list-dropzone"
            className="mt-0 grow overflow-auto outline-none focus:ring-0"
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            <DocumentManagerDragDrop
              dragUploaderRef={dragUploaderRef}
              handleDrop={handleDrop}
            />
            {(documentsAndFoldersLoading || !documentResponse) && (
              <>
                <div className="mb-4 h-6 w-full animate-pulse rounded border-b bg-gray-200" />
                <div className="mb-4 h-6 w-full animate-pulse rounded border-b bg-gray-200" />
                <div className="mb-4 h-6 w-full animate-pulse rounded border-b bg-gray-200" />
                <div className="mb-4 h-6 w-full animate-pulse rounded border-b bg-gray-200" />
                <div className="mb-4 h-6 w-full animate-pulse rounded border-b bg-gray-200" />
              </>
            )}
            {documentResponse?.documents?.length === 0 && (
              <div className="flex h-full items-center justify-center bg-[#e5e7eb]">
                <button
                  onClick={onClickUpload}
                  className="flex h-full w-full cursor-pointer items-center justify-center rounded-full border bg-gray-200 hover:text-gray-500"
                >
                  <div>
                    <div className="flex flex-col items-center">
                      <ArrowUpTrayIcon className="mb-8 h-16 w-16 text-blue-500" />
                      <div className="text-xl">
                        Click Here to Upload Documents
                      </div>
                      <div className="my-2 text-sm">or</div>
                      <div>Drag-and-drop files into this panel</div>
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
        )}
      </Dropzone>
      {files.length > 0 && uploaderVisible && (
        <div className="fixed bottom-0 right-5 min-w-[20rem] items-center justify-center rounded border bg-white p-2 pb-12 shadow">
          <div className="flex justify-between p-1">
            <div className="text-sm">Uploading</div>
            <button onClick={onCloseUploader}>
              <XMarkIcon className="h-5 w-5 cursor-pointer hover:text-gray-500" />
            </button>
          </div>
          <div className="mb-2 rounded bg-yellow-50 p-2 text-xs text-yellow-800">
            <strong>Warning:</strong> Navigating away from this page will cancel
            your uploads.
          </div>
          <div className="max-h-96 overflow-auto bg-white">
            {files.map((file) => {
              if (!file || !file.filename) {
                return null
              }

              const progressPercentage = calculateProgressPercentage(file)
              const isComplete = progressPercentage === 100
              const isFailed = fileStatus[file.filename] === 'failed'
              const isCancelled = fileStatus[file.filename] === 'cancelled'

              if (isCancelled) return null

              return (
                <div
                  key={`file_uploading_${file.id}`}
                  className="m-0.5 rounded bg-gray-100 p-3"
                >
                  <div className="mb-1 flex items-center justify-between">
                    <div
                      className="w-64 overflow-hidden text-ellipsis whitespace-nowrap text-sm"
                      data-tooltip-id="document-title-tooltip"
                      data-tooltip-content={file.filename}
                    >
                      {file.filename}
                    </div>

                    {isFailed && (
                      <button
                        onClick={() => retryUpload(file)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Retry
                      </button>
                    )}

                    {!isComplete && !isFailed && (
                      <button
                        onClick={() => cancelUpload(file)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Cancel
                      </button>
                    )}

                    {isComplete && !isFailed && (
                      <span className="text-xs text-green-600">Complete</span>
                    )}
                  </div>

                  {/* Progress bar section */}
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-grow overflow-hidden rounded-full bg-gray-200">
                      <div
                        className={`h-full rounded-full ${
                          isFailed
                            ? 'bg-red-600'
                            : isComplete
                              ? 'bg-green-600'
                              : 'bg-blue-600'
                        } transition-all ${
                          !fileProgressRef?.[file.filename]?.loaded
                            ? 'animate-pulse'
                            : ''
                        }`}
                        style={{
                          width: calculateProgressWidth(file),
                        }}
                      />
                    </div>

                    <div className="w-10 text-right text-xs text-gray-600">
                      {isFailed ? 'Failed' : `${progressPercentage}%`}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
      <div className="hidden">
        <Uploader
          setFiles={setFiles}
          files={files}
          ref={dragUploaderRef}
          onDocumentUploaded={onDocumentUploaded}
          onProgressUpdate={onProgressUpdate}
          onRegisterRequest={registerUploadRequest}
        />
      </div>
      <Tooltip id={'document-title-id'} style={{ zIndex: 100 }} />
    </div>
  )
}

export default DocumentList
